/* eslint-disable no-useless-escape */
/**
 * Format: Product
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API product responses.
 *
 */
import { formatImages } from '~/format/format-image'
import { formatMedia } from '~/format/format-media'
import { formatOptions } from '~/format/format-option'
import { formatProductPriceRange } from '~/format/format-price'
import { formatVariants } from '~/format/format-variant'
import { convertToHandle } from '~/helpers/convert'
import { decodeApiId, richtextToHtml, transformEdges } from '~/helpers/graphql'

/**
 * Format product.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product
 * @param {Object} response - GraphQL response.
 * @param {Object} [collection] - Formatted collection object.
 * @param {Object} [merge] - Product extension and grouping.
 * @returns {Object}
 */
export function formatProduct(response, { collection, merge = {} } = {}) {
  const product = Array.isArray(response)
    ? response[0]?.product
    : response?.product || response
  const productEnFields = Array.isArray(response)
    ? response[1]?.product || null
    : null

  let formattedProduct = formatBasicInfo(product, collection)

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct = {
    ...formattedProduct,
    ...formatFeaturedImages(product),
    ...formatVariantsAndOptions(product, formattedProduct),
    ...formatMetafields(product, productEnFields),
  }

  /**
   * Collection handle array.
   */
  const rawCollection = transformEdges(product?.collections).map((collectionObject) => {
    return {
      handle: collectionObject.handle,
      swatchBadge: collectionObject.swatchBadge || null,
    }
  })
  formattedProduct.collections = rawCollection

  formattedProduct.collectionsMetafields = transformEdges(product?.collections).map(
    (collectionObject) => {
      return collectionObject.swatchBadge
    },
  )

  /**
   * Preorder.
   */
  const preorderBySkuData = window.cnvs?.store.permanentDomain === '//veronica-beard-canada.myshopify.com/'
    ? product?.caPreorderBySku?.value
    : product?.preorderBySku?.value

  if (preorderBySkuData) {
    try {
      const preorderBySkuDataParsed = JSON.parse(preorderBySkuData)
      formattedProduct.preorderBySku = [...preorderBySkuDataParsed]
    } catch {
      window.console.warn('Error parsing pre-order variant data')
    }
  }

  /**
   * Format product grouping (colour swatches) and extensions (alt attributes).
   * - If response has grouping then it's a product card query and only needs
   *   the grouping data to render the swatches.
   * - Otherwise use the data from the product.merge template query.
   */
  if (product?.grouping) {
    formattedProduct.grouping = formatGrouping(product)
  } else {
    formattedProduct.extensions = merge.extensions || []
    formattedProduct.grouping = merge.grouping || []
  }

  return formattedProduct
}

/**
 * Initially format basic product info before handling images and metafields.
 * @param {Object} product
 * @param {Object} [collection] - Formatted collection object.
 */
function formatBasicInfo(response, collection) {
  const product = response?.product ? response.product : response

  const formattedProduct = {
    available: product?.available,
    compareAtPrice: formatProductPriceRange(product?.compareAtPriceRange),
    description: product?.descriptionHtml,
    giftCard: product?.giftCard,
    handle: product?.handle,
    id: decodeApiId(product?.id),
    media: formatMedia(product),
    price: formatProductPriceRange(product?.priceRange),
    tags: product?.tags,
    title: product?.title,
    type: product?.type,
    url: formatUrl(product?.handle, collection?.handle, product?.trackingParameters),
    variants: formatVariants(product, collection),
    vendor: product?.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product?.featuredImages))

  formattedProduct.featuredImage = featuredImages.length
    ? featuredImages[0]
    : featuredImages

  formattedProduct.secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  formattedProduct.options = formatOptions(product, formattedProduct.variants)

  formattedProduct.selectedOrFirstAvailableVariant =
    getSelectedOrFirstAvailableVariant(formattedProduct)

  return formattedProduct
}

/**
 * Get selected or first available variant.
 * - 'Selected' based on URL parameter.
 * - If no variants available then return first.
 * @param {Object} product - Formatted product object.
 * @returns {Object}
 */
function getSelectedOrFirstAvailableVariant(product) {
  const urlSearchParams = new URLSearchParams(location.search)

  const variantId = urlSearchParams.has('variant')
    ? urlSearchParams.get('variant')
    : false

  const selectedVariant = product?.variants.find((variant) => {
    if (variantId) {
      return variant.id === Number(variantId)
    }

    return variant.available
  })

  if (selectedVariant) {
    return selectedVariant
  }

  return product?.variants[0]
}

/**
 * Format product's featured and secondary images.
 * @param {Object} product
 * @returns {Object} Object with images to merge into product object.
 */
function formatFeaturedImages(product) {
  const featuredImages = formatImages(product, transformEdges(product?.featuredImages))

  const featuredImage = featuredImages.length
    ? featuredImages[0]
    : featuredImages

  const secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  return {
    featuredImage,
    secondaryImage,
  }
}

/**
 * Format product metafields.
 * @param {Object} product - Unformatted product
 * @returns {Object} Object with formatted values to merge into product object.
 */
function formatMetafields(product, productEnFields) {
  const specialtyBadge = product?.specialtyBadge?.value
  const specialtyBadgeFr = productEnFields?.specialtyBadgeFr?.value
  const shouldShowFrSpecialty = window.cnvs.store.language === 'fr' && productEnFields?.specialtyBadgeFr?.value

  const metafields = {
    shortDescription: product?.shortDescription?.value,
    bottomBadge: product?.bottomBadge?.value,
    discountCode: product?.discountCode?.value,
    fitNotes: product?.fitNotes?.value
      ? richtextToHtml(product?.fitNotes?.value)
      : null,
    fitGraphicOverallFit: product?.fitGraphicOverallFit?.value || null,
    fitGraphicScale: product?.fitGraphicScale?.value || null,
    fitGraphicLow: product?.fitGraphicLow?.value || null,
    fitGraphicMiddle: product?.fitGraphicMiddle?.value || null,
    fitGraphicHigh: product?.fitGraphicHigh?.value || null,
    secondaryName: product?.secondaryName?.value
      ? richtextToHtml(product?.secondaryName?.value)
      : null,
    specialtyBadge: shouldShowFrSpecialty ? specialtyBadgeFr : specialtyBadge,
    specialtyBadgeUrl: product?.specialtyBadgeUrl?.value,
    themeColor: product?.themeColor?.value,
    topBadge: product?.topBadge?.value,
  }

  metafields.flatImageIndex = product?.flatImageIndex
    ? Number(product?.flatImageIndex.value)
    : Number(cnvs.settings.product.flatImageIndex)

  metafields.matchingSet = {
    product: product?.matchingSetProduct?.value || null,
    description: product?.matchingSetDescription?.value || null,
    button: product?.matchingSetButton?.value || null,
  }

  metafields.dickeyJacketDescription = product?.dickeyBundleJacketDescription?.value || null
  metafields.categoryLabel = product?.categoryLabel?.value || null

  const reference = product?.qualityNotes?.reference
  if (!reference) {
    return { metafields }
  }

  const notes = []
  for (let index = 1; index <= 4; index++) {
    const media = parseMedia(reference[`image${index}`])
    const title = reference[`title${index}`]?.value || null
    const copy = reference[`copy${index}`]
      ? richtextToHtml(reference[`copy${index}`]?.value)
      : null

    notes.push({
      media,
      title,
      copy,
    })
  }
  metafields.qualityNotesTitle = product?.qualityNotesTitle?.reference?.sectionTitle?.value
  metafields.qualityNotes = notes

  return { metafields }
}

/**
 *
 * @param {Object} mediaField - Unformatted media metafield object
 * @returns Formatted media metafield object
 */
function parseMedia(mediaField) {
  const ref = mediaField?.reference
  if (!ref) {
    return null
  }

  const gid = ref.id || mediaField.value
  // eslint-disable-next-line no-underscore-dangle
  if (ref.__typename === 'MediaImage') {
    return {
      mediaType: 'image',
      id: decodeApiId(gid),
      image: {
        url: ref?.image?.url,
        alt: ref?.image?.altText,
        id: decodeApiId(gid),
      },
      alt: ref?.image?.altText || '',
    }
  }

  // eslint-disable-next-line no-underscore-dangle
  if (ref.__typename === 'Video') {
    return {
      mediaType: 'video',
      id: decodeApiId(gid),
      sources: ref.sources || [],
      previewImage: ref.previewImage?.url || '',
    }
  }

  return null
}

/**
 * Format variants and options for a product.
 * @param {Object} unformattedProduct - Original response product object
 * @param {Object} formattedProduct - Product object with formatting so far
 * @returns {Object} Object with formatted values to merge into product object.
 */
function formatVariantsAndOptions(unformattedProduct, formattedProduct) {
  const hasOnlyDefaultVariant = formattedProduct?.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  const options = formatOptions(unformattedProduct, formattedProduct.variants)

  const selectedOrFirstAvailableVariant =
    getSelectedOrFirstAvailableVariant(formattedProduct)

  return {
    hasOnlyDefaultVariant,
    options,
    selectedOrFirstAvailableVariant,
  }
}

/**
 * Format response URL with tracking parameters.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product#field-product-trackingparameters
 * @param {String} productHandle - Product handle
 * @param {Null|String} collectionHandle - Collection handle
 * @param {Null|String} [trackingParameters] - Response tracking parameters.
 * @returns {String}
 */
export function formatUrl(productHandle, collectionHandle, trackingParameters) {
  const url = collectionHandle
    ? `${cnvs.routes.normalisedRoot}collections/${collectionHandle}/products/${productHandle}`
    : `${cnvs.routes.normalisedRoot}products/${productHandle}`

  if (!trackingParameters) {
    return url
  }

  /**
   * Storefront API doesn't include https in URL but new URL() requires it.
   */
  const localUrl = `https://${url.split('//')[1]}`
  const urlObject = new URL(localUrl)
  const trackingSearchParams = new URLSearchParams(trackingParameters)

  const parameters = new URLSearchParams({
    ...Object.fromEntries(urlObject.searchParams),
    ...Object.fromEntries(trackingSearchParams),
  })

  if (!parameters.size) {
    return url
  }

  const href = urlObject.href
    .split('?')[0]
    .replace('https:', '')

  return `${href}?${parameters.toString()}`
}

/**
 * Format product grouping metafield.
 * @param {Object} response - Response product.
 * @returns {Array}
 */
function formatGrouping(response) {
  const fields = response.grouping?.reference?.fields
  const products = transformEdges(fields?.find((field) => field.key === 'products')?.references)
  const category2Products = transformEdges(fields?.find((field) => field.key === 'category_2_products')?.references)
  const allProducts = [
    ...products,
    ...category2Products,
  ]

  return allProducts.map((product) => formatProductMerge(product, response.grouping))
}

/**
 * Format product merge from product grouping metafield.
 * @param {Object} product - Nested product.
 * @returns {Array}
 */
function formatProductMerge(product, grouping) {

  /**
   * Do not render if unavailable and has final sale tag.
   */
  let render = true

  const finalSale = ['finalsale', 'final sale', 'final_sale', 'final-sale'].some((saleTag) => {
    return product?.tags?.find((tag) => tag.toLowerCase() === saleTag)
  })

  if (!product?.available && finalSale) {
    render = false
  }

  /**
   * Swatch data.
   */
  const colour = ['Color', 'Colour', 'Couleur'].includes(product?.options?.[0]?.name)
    ? product?.options?.[0]?.values?.[0]
    : false

  const swatchLabel = product?.swatchLabel?.value
  const swatchFile = product?.swatchFile?.reference?.image?.url
  const swatchName = colour || swatchLabel
  const swatchNameHandle = convertToHandle(`${swatchName}-${product.handle}`)

  const swatchCategories = []

  const fields = grouping?.reference?.fields
  const category1name = fields.find(field => field.key === 'category_1_name')?.value
  const category2name = fields.find(field => field.key === 'category_2_name')?.value

  const category1ProductsField = fields?.find((field) => field.key === 'products')
  const category2ProductsField = fields?.find((field) => field.key === 'category_2_products')

  const cat1products = category1ProductsField?.value
  const cat2products = category2ProductsField?.value

  if (cat1products?.includes(product.id) && category1name) {
    swatchCategories.push(category1name)
  }

  if (cat2products?.includes(product.id) && category2name) {
    swatchCategories.push(category2name)
  }

  const formattedObject = {
    metaobject: {},
    product: {
      available: product.available,
      handle: product.handle,
      id: decodeApiId(product.id),
    },
    render,
    swatch: {
      file: swatchFile,
      handle: swatchNameHandle,
      label: swatchLabel,
      name: swatchName,
      src: swatchFile,
      categories: swatchCategories,
    },
    type: 'grouping',
  }

  return formattedObject
}
